'use client';

import { getDictionary } from '@/app/dictionaries/getDictionary';
import { AgentLogger } from '@/utils/logger-agent'; // New import
import { sendErrorToSlack } from '@/utils/slack-error-reporter';
import { Chat } from '@/utils/types';
import { XMarkIcon } from '@heroicons/react/24/solid';
import Image from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AdditionalInfoForm from './AdditionalInfoForm';
import { AddressForm } from './AddressForm';
import { getAgencyConfig } from './agency-config-helper'; // Import AgencyConfiguration
import ContactForm from './ContactForm';
import { IntroForm } from './IntroForm';
import MapTimeframeForm from './MapTimeframeForm';
import PropertyDetailsForm from './PropertyDetailsForm';
import PropertyTransactionForm from './PropertyTransactionForm';
import ReasoningLoader from './ReasoningLoader';
import ValuationForm from './ValuationForm';

// Device detection utilities
const detectDeviceType = (): 'mobile' | 'desktop' => {
  return window.innerWidth <= 768 ? 'mobile' : 'desktop';
};

const detectBrowser = (): string => {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Chrome')) return 'chrome';
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'safari';
  if (userAgent.includes('Firefox')) return 'firefox';
  if (userAgent.includes('Edge')) return 'edge';
  return 'other';
};

const getParentUrl = (): string => {
  try {
    // Get URL from parent window (where widget is embedded)
    return window.parent.location.href;
  } catch (error) {
    // Fallback to current URL if cross-origin
    return window.location.href;
  }
};

const getTrackingParams = (url: string): Record<string, string> => {
  try {
    const urlObj = new URL(url);
    const trackingParams: Record<string, string> = {};
    
    // Standard UTM parameters
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
      const value = urlObj.searchParams.get(param);
      if (value) trackingParams[param] = value;
    });
    
    // Google and Facebook click IDs
    ['gclid', 'fbclid'].forEach(param => {
      const value = urlObj.searchParams.get(param);
      if (value) trackingParams[param] = value;
    });
    
    return trackingParams;
  } catch (error) {
    return {};
  }
};

// Move this function inside the component so it can access state
const captureLeadSourceData = (leadSourceDataFromWidget: any) => {
  // If we have lead source data from the widget loader, use it (it has the correct parent URL)
  if (leadSourceDataFromWidget) {
    return leadSourceDataFromWidget;
  }
  
  // Fallback to our own detection if widget loader data is not available
  const deviceType = detectDeviceType();
  const browser = detectBrowser();
  const sourceUrl = getParentUrl();
  const trackingParams = getTrackingParams(sourceUrl);
  
  // Determine utmSource based on click IDs
  let utmSource = trackingParams.utm_source;
  let utmMedium = trackingParams.utm_medium;
  const utmCampaign = trackingParams.utm_campaign;
  const utmTerm = trackingParams.utm_term;
  const utmContent = trackingParams.utm_content;
  
  // Override with click ID data
  if (trackingParams.gclid) {
    utmSource = 'google';
    utmMedium = 'cpc';
    // Could potentially extract campaign info from gclid in the future
  } else if (trackingParams.fbclid) {
    utmSource = 'facebook';
    utmMedium = 'cpc';
    // Could potentially extract campaign info from fbclid in the future
  }
  
  return {
    deviceType,
    browser,
    sourceUrl,
    utmSource,
    utmMedium,
    utmCampaign,
    utmTerm,
    utmContent,
    gclid: trackingParams.gclid,
    fbclid: trackingParams.fbclid
  };
};

// Define the updated sequence of steps for the progress bar
const AVM_STEPS = [
  'sales-avm-step1-intro',
  'sales-avm-step2-address',
  'sales-avm-step3-statbel',
  'sales-avm-step4-transaction',
  'sales-avm-step5-additional-info',
  'sales-avm-step6-map-timeframe',
  'sales-avm-step7-contact',
  'sales-avm-step8-valuation',
  'sales-avm-step9-final-evaluation',
];
const TOTAL_AVM_STEPS = AVM_STEPS.length;

type Message = {
  role: 'user' | 'assistant' | 'system';
  content: string;
  followUpQuestions?: string[];
  chatId?: string;
  createdAt?: Date;
  userId?: string;
  classification?: {
    primary_intent: string;
    workflowStep?: string;
  };
  workflowStep?: string;
  id?: string;
  properties?: { 
    city?: string;
    propertyType?: string;
    livingArea?: number;
    bedrooms?: number;
    bathrooms?: number;
    plotArea?: number;
    [key: string]: any;
  };
};

interface ChatConfig {
  agencyId?: string;
  agentId?: string;
  backgroundColor?: string;
  faviconUrl?: string;
  enableMfa?: boolean;
  agencyEmail?: string;
  agencyName?: string;
  agencyLanguage?: string;
  language?: string;
  textStyle?: React.CSSProperties;
  defaultLocale?: 'nl' | 'fr' | 'en';
  styling?: React.CSSProperties;
  startOpen?: boolean;
  // agency configuration
  top?: React.ComponentType<any>;
  left?: React.ComponentType<any>;
  widgetTitleOverrideKey?: string;
  headerTitleOverrideKey?: string;
  // Other configuration
  options?: {
    animationDuration?: number;
  };
  closedWidgetStyling?: React.CSSProperties; // Add optional styling for closed widget
  swapLogoWithAI?: boolean; // Add flag for swapping logo
  swapLogoWithArrow?: boolean; // Add flag for swapping logo and arrow positions
  closedWidgetCtaConfig?: { // Add config for CTA button
    useIcon?: boolean;
    colorClassName?: string;
    AIwhite?: boolean;
  };
  headerStyling?: { // Add config for header styling
    type?: 'gradient' | 'corner';
    backgroundColor?: string;
    textColor?: string;
    background?: string;
    backgroundBlendMode?: string;
  };
  closedWidgetCloseButtonClassName?: string; // Add optional className for close button
  closedWidgetCloseIconClassName?: string; // Add optional className for close icon
  preventRefreshOnUrlChange?: boolean; // Add optional flag for preventing refresh
  enableEmailCollection?: boolean; // Add optional flag for email collection
  enableEmailCollectionOnlyAgency?: boolean; // Add optional flag for agency-only email collection
  customEmailMessage?: string; // Add custom email message
  customEmailPrompt?: string; // Add custom email prompt
  customThankYouMessage?: string; // Add custom thank you message
  aiChipTextOverrideKey?: string; // Add optional key for AI chip text override
  contactPromptOverrideKey?: string;
  excludedTimeframeOptions?: ('now' | '6m' | '12m' | 'curious' | 'no')[];
  // Custom valuation component
  enableCustomValuationComponent?: boolean;
  customValuationMessage?: string;
  customValuationButton?: string;
  customValuationTimer?: number; // Timer in milliseconds
  // Agent contact notice control
  agentContactNoticeNull?: boolean; // If true, hides the agent contact notice text
  isDismissed?: boolean; // Add optional flag for minimized state
  hideWidgetWhenDismissed?: boolean; // Add optional flag for hiding widget when dismissed
}

interface ChatProps {
  defaultLocale: string;
}

// Define the structure for comparable properties
interface ComparableProperty {
  id: string;
  lat: number;
  lng: number;
  price: number;
  address: string;
  livingArea: number;
  bedrooms: number;
  energyClass: string;
  plotSurface: number;
  buildingConditionScore: number;
  numberOfFrontages: number;
}

// Define ContactInfo type used in Step 6
interface ContactInfo {
  name?: string; // Add optional name
  phone: string;
  email?: string;
}

// Create a logger instance for AVM agent
const logger = new AgentLogger('avm-agent');

// Simple error handler for AvmAgent
const handleAvmError = (error: any, context: string, additionalData?: any) => {
  console.error(`Error in AvmAgent (${context}):`, error);
  
  // Send to Slack
  sendErrorToSlack({
    error: {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      type: 'AvmAgentError'
    },
    context: {
      endpoint: `/app/[locale]/avm/_components/AvmAgent - ${context}`,
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV || 'development'
    },
    userData: {
      guestId: additionalData?.guestId,
      chatId: additionalData?.chatId,
      agencyId: additionalData?.agencyId
    },
    requestPayload: {
      context,
      additionalData,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  });
};

function AvmAgent({ defaultLocale }: ChatProps) {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // --- Add ref for the last message ---
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dictionary, setDictionary] = useState<any>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const [chatId, setChatId] = useState<string | undefined>(undefined);
  const [guestId, setGuestId] = useState<string | undefined>(undefined);
  const [detectedLanguage, setDetectedLanguage] = useState<string>('nl'); // Default to English
  const [urlConfig, setUrlConfig] = useState<ChatConfig | null>(null);
  const [locale, setLocale] = useState<string>(defaultLocale || 'nl');
  const [agentId, setAgentId] = useState<string | null>(null);
  const [chatBackgroundColor, setChatBackgroundColor] = useState<string>('#ffffff'); // State for background color, default to white
  const [chatTextStyle, setChatTextStyle] = useState<React.CSSProperties>({});
  const [isReady, setIsReady] = useState(false); // State to track initial loading
  const [chatFaviconUrl, setChatFaviconUrl] = useState<string | null>(null); // State for favicon URL, default null
  const [avmFetchPromise, setAvmFetchPromise] = useState<Promise<any> | null>(null); // State to hold the background AVM fetch promise
  // --- Add state for phone verification persistence ---
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  // --- Add state for tracking flow completion ---
  const [isFlowCompleted, setIsFlowCompleted] = useState(false);
  // --- Add state for minimized circular button ---
  const [isMinimized, setIsMinimized] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // --- Add comprehensive loading state ---
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  // --- Add state to store lead source data from widget loader ---
  const [leadSourceDataFromWidget, setLeadSourceDataFromWidget] = useState<any>(null);
  // --- Add state to prevent initial render before first URL is processed ---
  const [initialUrlProcessed, setInitialUrlProcessed] = useState(false);

  // --- Refs for timers and flags are no longer needed with server-side orchestration ---

  const [sellingState, setSellingState] = useState<{
    contactInfo?: ContactInfo; // Updated type
    privacyConsent?: boolean;
    propertyInfo?: {
      epcValue: string;
      condition: string;
      asbestos: string;
      finishLevel: string;
    };
    propertyDetails?: {
      livingArea: number;
      bedrooms: number;
      bathrooms: number;
      plotArea?: number;
      facade?: number;
      transactionCount?: number;
    };
    streetName?: string;
    houseNumber?: string;
    zipCode?: string;
    city?: string;
    propertyType?: 'apartment' | 'house';
    isInterestedInValuation?: boolean;
    additionalDetails?: any;
    priceRange?: { min: number; max: number };
    avmResult?: any;
    comparables?: ComparableProperty[];
    subject_latitude?: number;
    subject_longitude?: number;
    latitude?: number;
    longitude?: number;
    // --- Add star rating to state --- 
    starRating?: number;
    // --- Add flag for details response (optional, added in previous step but ensure it's here) ---
    wantsDetails?: boolean;
    // --- Add selling timeframe ---
    sellingTimeframe?: 'now' | '6m' | '12m' | 'curious' | 'no';
    // --- Add lead source data for tracking ---
    leadSourceData?: {
      deviceType: 'mobile' | 'desktop';
      browser: string;
      sourceUrl: string;
      utmSource?: string;
      utmMedium?: string;
      utmCampaign?: string;
      utmTerm?: string;
      utmContent?: string;
      gclid?: string;
      fbclid?: string;
    };
  }>({});

  useEffect(() => {
    // Let the parent know the widget is ready to receive initial data
    window.parent.postMessage({ type: 'AVM_WIDGET_READY' }, '*');
  }, []);

  const sellingStateRef = useRef(sellingState);
  useEffect(() => {
    sellingStateRef.current = sellingState;
  }, [sellingState]);

  // Update locale when defaultLocale prop changes
  useEffect(() => {
    if (defaultLocale) {
      setLocale(defaultLocale);
    }
  }, [defaultLocale]);

  // Function to get config from URL parameters
  const getConfigFromUrl = (): ChatConfig => {
    if (typeof window === 'undefined') return {};
  
    try {
    // Try URL parameters
      const params = new URLSearchParams(window.location.search);
      const encodedParentUrl = params.get('config');
      if (encodedParentUrl) {
        const config = JSON.parse(encodedParentUrl);
        return config;
      }
    } catch (error) {
      console.error('Error parsing config from URL:', error);
    }
    return {};
  };

  // Initialize minimized state from config
  useEffect(() => {
    const config = getConfigFromUrl();
    if (config.isDismissed) {
      setIsMinimized(true);
    }
  }, []);

  const refreshChat = useCallback(() => {
    setHasInitialized(false);
    setIsChatOpen(false);
    setChatId(undefined);
    setGuestId(undefined);
    setMessages([{ // Reset to the initial state (IntroForm trigger)
      role: 'assistant',
      content: '|SHOW_SELLING_FORM',
      classification: {
        primary_intent: 'initiate_sales_avm',
        workflowStep: 'sales-avm-step1-intro'
      },
      workflowStep: 'avm_form'
    }]);
  }, []);
  
  // Effect for one-time initialization of config and agency data
  useEffect(() => {
    const initialize = async () => {
      let initialConfig: any = {};
      
      try {
        const config = getConfigFromUrl();
        const agencyConfig = getAgencyConfig(config.agencyId);
        initialConfig = { ...config, ...agencyConfig };

        if (initialConfig.agencyId) {
          try {
            const agencyResponse = await fetch(`/api/agency?agencyId=${initialConfig.agencyId}`);
            if (agencyResponse.ok) {
              const agencyData = await agencyResponse.json();
              // Merge fetched data, preserving any initial URL params over fetched data if they conflict
              initialConfig = { ...agencyData, ...initialConfig };
            }
          } catch (error) {
            console.error('Error fetching agency data:', error);
          }
        }
        
        setUrlConfig(initialConfig);
        setIsChatOpen(initialConfig?.startOpen ?? false);

        // Set initial message right away
        setMessages([{
          role: 'assistant',
          content: '|SHOW_SELLING_FORM',
          classification: {
            primary_intent: 'initiate_sales_avm',
            workflowStep: 'sales-avm-step1-intro'
          },
          workflowStep: 'avm_form'
        }]);

        // Mark config as loaded
        setIsConfigLoaded(true);
      } catch (error) {
        handleAvmError(error, 'Initialization', { agencyId: initialConfig?.agencyId });
        // Still mark as loaded even if there's an error to prevent infinite loading
        setIsConfigLoaded(true);
      }
    };

    initialize();
  }, []); // Run only once on mount

  // Effect for fetching data that depends on locale or when config is ready
  useEffect(() => {
    // Wait for config AND the first URL message from the parent before doing anything
    if (!urlConfig || !initialUrlProcessed) return;

    const fetchDependentData = async () => {
      if (!urlConfig.agentId) {
        console.error('Agent ID missing');
        // Potentially set an error state for the user
        return;
      }

      // Set state based on the now-stable urlConfig
      setAgentId(urlConfig.agentId);
      setChatBackgroundColor(urlConfig?.backgroundColor ?? '#ffffff');
      setChatFaviconUrl(urlConfig?.faviconUrl ?? null);
      setChatTextStyle(urlConfig?.textStyle ?? {});
      
      // Important: Use the locale from state, but default to config if locale hasn't been changed by parent
      const currentLocale = locale || urlConfig?.defaultLocale || 'nl';
      if (locale !== currentLocale) {
        setLocale(currentLocale);
      }

      let questions = null;
      if (urlConfig.agencyId) {
        try {
          const response = await fetch(`/api/questions/${urlConfig.agencyId}?locale=${currentLocale}`);
          if (response.ok) {
            questions = await response.json();
          }
        } catch (error) {
          // Silently handle any errors - questions will remain null and defaults will be used
        }
      }
      
      const dict = await getDictionary(currentLocale, questions);
      setDictionary(dict);
      setIsReady(true); // Now we are ready to show
    };

    fetchDependentData();
  }, [urlConfig, locale, initialUrlProcessed]);

  useEffect(() => {
    if (!isLoading) {
      if (inputRef.current) {
        inputRef.current?.focus(); // Focus after a slight delay
      }
    }
  }, [isLoading]);

  // Always scroll to bottom on new messages
  // useEffect(() => {
  //   // --- Use scrollIntoView on the last message element --- 
  //   if (lastMessageRef.current) {
  //     // Simple scrollIntoView call
  //     lastMessageRef.current.scrollIntoView(); 
  //   }
  // }, [messages]); // Dependency array includes messages
  // ---- REMOVED this hook as MutationObserver handles it ----

  // --- Auto-scroll effect using MutationObserver ---
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Function to scroll smoothly to the bottom
    const scrollToBottom = () => {
      // Use requestAnimationFrame to wait for next paint cycle
      requestAnimationFrame(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      });
    };

    // Initial scroll to bottom when the effect runs
    scrollToBottom();

    // Create an observer instance linked to a callback function
    const observer = new MutationObserver((mutationsList) => {
      // --- Check if ANY mutation in this batch originated from the map ---
      let shouldScroll = true; // Assume we should scroll by default
      for (const mutation of mutationsList) {
        // Check if the mutation target or its parent is the map area
        if (mutation.target instanceof Node && mutation.target.nodeType === Node.ELEMENT_NODE) {
          // Check the target element itself
          if ((mutation.target as HTMLElement).closest('.google-map-interaction-area')) {
            shouldScroll = false;
            break; // Found a map mutation, no need to check others in this batch
          }
        }
        // Also check added nodes, as the target might be the messages container
        // but a map node was added
        for (const addedNode of mutation.addedNodes) {
          if (addedNode instanceof HTMLElement && addedNode.closest('.google-map-interaction-area')) {
            shouldScroll = false;
            break; // Found a map mutation
          }
        }
        // If we've decided not to scroll, exit the outer loop too
        if (!shouldScroll) break; 
      }

      // --- Only scroll if no map-related mutations were detected ---
      if (shouldScroll) {
        scrollToBottom();
      }
    });

    // Start observing the target node for configured mutations
    // --- Observe attributes and characterData as well ---
    const config = { childList: true, subtree: true, attributes: true, characterData: true };
    observer.observe(container, config);

    // Cleanup function to stop observing when the component unmounts
    return () => {
      observer.disconnect();
    };
  }, [messages]); // Re-run when messages array changes to ensure observer is attached
  // --- End Auto-scroll effect ---

  // Save message to DB
  const saveMessage = useCallback(async (message: Message) => {
    if (!message.chatId || !message.userId) {
      console.error('Cannot save message:', message);
      return;
    }

    try {
      await fetch('/api/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...message,
          createdAt: new Date(),
        }),
      });
    } catch (error) {
      console.error('Error saving message:', error);
    }
  }, []);

  const formatMessage = useCallback((content: string) => {
    // Handle bold text
    content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Clean up multiple newlines first
    content = content.replace(/\n{3,}/g, '\n\n'); // Replace 3+ newlines with 2
    
    // Preserve double line breaks and handle bullet points
    return content
      .split(/\n\n/) // Split on double line breaks first
      .map(paragraph => {
        if (paragraph.trim() === '') return '<div class="h-4"></div>'; // Increased spacing
        const lines = paragraph.split('\n').map(line => {
          line = line.trim(); // Trim each line
          if (line.startsWith('• ')) return `<div class="pl-2">${line}</div>`;
          if (line.startsWith('○ ')) return `<div class="pl-4">${line}</div>`;
          if (line.startsWith('- ')) return `<div class="pl-8">${line.replace(/^- /, '- ')}</div>`; // Add more indent for sub-sub-points
          return `<div>${line}</div>`;
        });
        return lines.join('');
      })
      .join('<div class="h-4"></div>'); // Add spacing between paragraphs
  }, []);

  const minimizeChat = () => setIsChatOpen(false);

  // Helper function to handle the deterministic form transition
  const handleDeterministicStep = async (currentStep: string, userMessageContent: string, formData?: any) => {
    try {
      setIsLoading(true);

      const newUserMessage: Message = {
        role: 'user',
        content: userMessageContent,
        chatId: chatId,
        userId: guestId,
        classification: { primary_intent: 'provide_data', workflowStep: currentStep },
        workflowStep: 'avm_form'
      };

      if (!hasInitialized) {
        logger.error('Chat not initialized when trying to submit form data.', { currentStep });
        setError('Chat session error. Please refresh.');
        setIsLoading(false);
        return;
      }
      await saveMessage(newUserMessage);
      setMessages(prev => [...prev, newUserMessage]);

      let nextStep: string | null = null;
      let avmResultData: any = null; // To store AVM result if called

      // Special handling for end of step 6 (Contact Info)
      if (currentStep === 'sales-avm-step6-contact') {
        try {
        // Construct payload for /api/avm
          const avmPayload = {
          // Ensure all necessary data from sellingState is included
          // Use defaults or handle missing data gracefully
            'Postal Code': sellingState.zipCode || '',
            'Living Area': Number(sellingState.propertyDetails?.livingArea) || 0,
            'Building Condition Score': sellingState.propertyInfo?.condition ? parseInt(sellingState.propertyInfo.condition) : 3,
            'Property Type': sellingState.propertyType || 'house',
            'Address': `${sellingState.streetName} ${sellingState.houseNumber}, ${sellingState.zipCode} ${sellingState.city}`,
            'Plot Surface': sellingState.propertyDetails?.plotArea || 0,
            'New Construction': false, // Assuming false unless captured
            'Energy Class': sellingState.propertyInfo?.epcValue || 'unknown',
            'Asbestos': sellingState.propertyInfo?.asbestos,
            'Finish Level': sellingState.propertyInfo?.finishLevel,
            'Number Of Frontages': sellingState.propertyDetails?.facade || (sellingState.propertyType === 'apartment' ? 2 : 4),
            'Bedrooms': sellingState.propertyDetails?.bedrooms || 0,
            'Latitude': sellingState.latitude,
            'Longitude': sellingState.longitude,
          // Add any other required fields for your /api/avm endpoint
          };

          // --- Add a check/log for missing coordinates --- START
          if (sellingState.latitude === undefined || sellingState.longitude === undefined) {
            logger.warn('Latitude or Longitude missing from sellingState before sending AVM payload', {
              latitude: sellingState.latitude,
              longitude: sellingState.longitude,
              currentState: sellingState
            });
          // Potentially add error handling here if needed, though the Python script might still geocode
          }
          // --- Add a check/log for missing coordinates --- END

          const avmResponse = await fetch('/api/avm', { // Assuming this is your AVM endpoint
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ messages: [{ role: 'user', content: JSON.stringify(avmPayload) }] }) // Adjust payload structure if needed
          });

          avmResultData = await avmResponse.json(); // Store the full result
          if (!avmResponse.ok) {
            throw new Error(`AVM API returned ${JSON.stringify(avmResultData)}`);
          }

          // Extract price range (adjust based on actual API response structure)
          const priceRange = avmResultData?.priceRange || avmResultData?.valuation?.priceRange || { min: 0, max: 0 };
          setSellingState(prev => ({ ...prev, priceRange, avmResult: avmResultData }));

          // Now call workflow to get the next step (should be step 7)
          const workflowResponse = await fetch('/api/avm/workflow', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ currentStep }),
          });

          if (!workflowResponse.ok) {
            throw new Error(`Workflow API returned ${workflowResponse.status} after AVM call`);
          }
          const workflowData = await workflowResponse.json();
          nextStep = workflowData.nextStep; // Should be 'sales-avm-step7-valuation'

        } catch (avmError) {
          logger.error('Error during AVM call after step 6:', avmError);
          setError('Failed to get property valuation. Please try again or contact support.');
          setIsLoading(false);
          return;
        }
      } else {
      // For all other steps, just call the workflow endpoint
        try {
          const workflowResponse = await fetch('/api/avm/workflow', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ currentStep }),
          });

          if (!workflowResponse.ok) {
            throw new Error(`Workflow API returned ${workflowResponse.status}`);
          }
          const workflowData = await workflowResponse.json();
          nextStep = workflowData.nextStep;
        } catch (error) {
          logger.error('Error calling workflow endpoint or processing response:', error);
          setError('Failed to determine next step. Please try again.');
          setIsLoading(false);
          return;
        }
      }

      // Handle the next step
      if (nextStep && nextStep === 'sales-avm-step9-final-evaluation') {
      // --- If workflow says next is Step 9, trigger final message ---
        // Format the final message using the priceRange from the current state
        const finalMessageContent = (dictionary?.sellingForm?.finalThankYou || 
          'Thank you! Your valuation estimate is between €[MIN_PRICE] and €[MAX_PRICE]. We will be in touch.')
          .replace('[MIN_PRICE]', sellingState.priceRange?.min?.toLocaleString(['nl-BE']) ?? 'N/A')
          .replace('[MAX_PRICE]', sellingState.priceRange?.max?.toLocaleString(['nl-BE']) ?? 'N/A');
          
        const finalAssistantMessage: Message = {
          role: 'assistant',
          content: finalMessageContent,
          chatId: chatId,
          userId: agentId as string,
          classification: { primary_intent: 'sales_avm_complete', workflowStep: 'completed' }
        };

        await new Promise(resolve => setTimeout(resolve, 500)); // Small delay
        setMessages(prev => [...prev, finalAssistantMessage]);
        await saveMessage(finalAssistantMessage);
        setIsLoading(false);

      } else if (nextStep) { // If workflow provides a different next step (next form to show)
      // --- Standard next step logic ---
        const nextAssistantMessage: Message = {
          role: 'assistant',
          content: '|SHOW_SELLING_FORM', 
          chatId: chatId,
          userId: agentId as string,
          classification: { primary_intent: 'sales_avm', workflowStep: nextStep },
          workflowStep: 'avm_form'
        // Optionally add AVM result to message if needed for display
        // properties: nextStep === 'sales-avm-step7-valuation' ? avmResultData : undefined
        };

        // Add a delay for visual feedback
        await new Promise(resolve => setTimeout(resolve, 800));

        // Directly update UI and save
        setMessages(prev => [...prev, nextAssistantMessage]);
        await saveMessage(nextAssistantMessage);
        setIsLoading(false);

      } else if (nextStep === null && currentStep === 'sales-avm-step9-final-evaluation') {      // --- This block is now effectively handled by the logic above --- 
      // Keep it potentially as a fallback or remove if confident.
      // For now, let's log if we ever reach here unexpectedly.
        logger.warn('Reached final evaluation processing block unexpectedly.');
        // ... existing final message logic ... (can likely be removed)
        setIsLoading(false);
      } else {
      // Unexpected response from workflow API or end condition met prematurely
        logger.warn(`Workflow endpoint returned unexpected step: ${nextStep} from ${currentStep}.`);
        setError('Could not determine the next step automatically.');
        setIsLoading(false);
      }
    } catch (error) {
      handleAvmError(error, 'handleDeterministicStep', { 
        currentStep, 
        chatId, 
        guestId, 
        agencyId: urlConfig?.agencyId 
      });
      setError('An error occurred while processing your request. Please try again.');
      setIsLoading(false);
    }
  };

  const renderSellingForm = (message: Message) => {
    const currentMessage = messages[messages.length - 1];
    const isCurrentMessage = message === currentMessage;


    if (!message.content?.includes('|SHOW_SELLING_FORM')) {
      return null;
    }

    // Ensure dictionary is loaded before rendering any specific form
    if (!dictionary) {
      logger.warn('Dictionary not loaded, cannot render form');
      return <div className="text-sm text-gray-500">Loading form...</div>; // Or some placeholder
    }

    switch (message.classification?.workflowStep) {
      case 'sales-avm-step1-intro':

        return (
          <IntroForm
            dictionary={dictionary}
            agencyId={urlConfig?.agencyId}
            onSubmit={async () => {
              const currentStep = 'sales-avm-step1-intro';
              
              const readyResponseText = dictionary?.sellingForm?.readyResponse || 'Yes';

              // Create the user message object first
              const newUserMessage: Message = {
                role: 'user' as const,
                content: readyResponseText,
                chatId: chatId, // Use state chatId (might be undefined initially)
                userId: guestId, // Use state guestId (might be undefined initially)
                classification: { primary_intent: 'affirm', workflowStep: currentStep },
                workflowStep: 'avm_form'
              };

              // --- Optimized Immediate UI Update (User Message Only) --- 
              setMessages(prev => [...prev, newUserMessage]);
              setIsLoading(true); // Start loading indicator immediately
              
              // --- Delayed UI Update (Next Form) & Background Processing --- 
              
              // Define the background task function
              const initializeAndSave = async () => {
                let definitiveChatId = chatId;
                let definitiveGuestId = guestId;
                let justInitialized = false;
                try {
                  // 1. Initialize if needed (can run concurrently with timeout)
                  if (!hasInitialized) {
                    if (!agentId) throw new Error('Agent ID missing during chat initialization');
                    
                    const initResponse = await fetch('/api/conversations', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ agencyId: urlConfig?.agencyId, agentId, type: 'CHAT' } as Chat),
                    });
                    if (!initResponse.ok) {
                      throw new Error(`Failed to init chat via API: ${initResponse.statusText}`);
                    }
                    
                    const chatData = await initResponse.json();
                    definitiveChatId = chatData.chatId;
                    definitiveGuestId = chatData.guestId;
                    justInitialized = true; 
                    


                    // Update state *after* successful initialization
                    setChatId(definitiveChatId);
                    if (!localStorage.getItem('chatId')) {
                      localStorage.setItem('chatId', definitiveChatId as string);
                    }
                    setGuestId(definitiveGuestId);
                    setHasInitialized(true); 
                  }

                  // 2. Save the user message (now with definitive IDs)
                  if (definitiveChatId && definitiveGuestId) {
                    await saveMessage({...newUserMessage, chatId: definitiveChatId, userId: definitiveGuestId });
                  } else {
                    logger.error('Cannot save user message, missing chatId or guestId after initialization attempt');
                    throw new Error('Chat identification failed.'); // Throw to prevent saving assistant msg
                  }

                  // 3. Prepare the assistant message for the next step
                  const nextAssistantMessage: Message = {
                    role: 'assistant',
                    content: '|SHOW_SELLING_FORM',
                    chatId: definitiveChatId, // Use the definitive chatId
                    userId: agentId as string, 
                    classification: { primary_intent: 'sales_avm', workflowStep: 'sales-avm-step2-address' },
                    workflowStep: 'avm_form'
                  };

                  // 4. Save the assistant message (using definitive IDs)
                  if (definitiveChatId && agentId) {
                    await saveMessage(nextAssistantMessage);
                  } else {
                    logger.error('Cannot save assistant message, missing chatId or agentId');
                    // Potentially throw an error here too
                  }

                  // Return the prepared assistant message for the timeout callback
                  return nextAssistantMessage;

                } catch (error) {
                  logger.error('Error during intro form background processing:', error);
                  setError(`Failed to initialize or save initial messages: ${error instanceof Error ? error.message : 'Unknown error'}`);
                  setIsLoading(false); // Ensure loading stops on error
                  return null; // Indicate failure
                }
              };

              // Start the background task immediately
              const backgroundPromise = initializeAndSave();

              // --- Introduce a 1-second delay before showing the next form --- 
              setTimeout(async () => {
                try {
                  // Wait for the background task to potentially finish getting IDs and prepare the message
                  const nextAssistantMessage = await backgroundPromise;

                  if (nextAssistantMessage) {
                    // Append the Step 6 form message instead of replacing
                    setMessages(prev => [...prev, nextAssistantMessage]);
                  } else {
                    // If backgroundPromise resolved to null (error occurred), 
                    // ensure loading is false, error state should already be set.
                    logger.warn('Background task failed, not showing next step form.');
                  }
                } catch (error) {
                  // Catch potential errors from the backgroundPromise itself if it rejects unexpectedly
                  logger.error('Error resolving background task promise in timeout:', error);
                  setError('An unexpected error occurred while preparing the next step.');
                  setIsLoading(false);
                } finally {
                  // Set loading false *after* the timeout and potential UI update, 
                  // only if the background task didn't already set it false due to error.
                  // However, the background task's finally block might run after this in some cases.
                  // Let's rely on the background task's finally block to set loading false.
                  //setIsLoading(false); 
                  // Correction: We need isLoading to be false *after* the form appears to enable it.
                  // The background task's finally block handles the case where init/save finishes *before* the timeout.
                  // This timeout's finally block handles the case where init/save finishes *after* the timeout.
                  const finalAssistantMessage = await backgroundPromise.catch(() => null); // Re-check promise state
                  if (finalAssistantMessage) { // Only set loading false if background task succeeded
                    setIsLoading(false); 
                  }
                }
              }, 800); // 800ms = 0.8 second delay

            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );
      case 'sales-avm-step2-address':

        return (
          <AddressForm
            dictionary={dictionary}
            onSubmit={({ streetName, houseNumber, zipCode, city, propertyType, latitude, longitude }) => {
              const currentStep = 'sales-avm-step2-address';
              
              // --- Capture lead source data here (delayed until Step 2) ---
              if (!sellingState.leadSourceData) {
                const leadSourceData = captureLeadSourceData(leadSourceDataFromWidget);
                setSellingState(prev => ({ 
                  ...prev, 
                  leadSourceData 
                }));
              }
              
              const address = `${streetName} ${houseNumber}, ${zipCode} ${city}`;
              setSellingState(prev => ({ 
                ...prev, 
                streetName,
                houseNumber, 
                zipCode, 
                city, 
                propertyType, 
                latitude,
                longitude
              }));
              const formattedMessage = dictionary?.sellingForm ? 
                `${dictionary.sellingForm.propertyProvided || 'Here is my property information:'}\n• ${dictionary.sellingForm.propertyAddress || 'Address'}: ${address}\n• ${dictionary.sellingForm.propertyType || 'Property type'}: ${propertyType === 'apartment' ? dictionary.sellingForm.propertyTypeApartment : dictionary.sellingForm.propertyTypeHouse}` :
                `Here is my property information:\n• Address: ${address}\n• Property type: ${propertyType}`;
              handleDeterministicStep(currentStep, formattedMessage, { streetName, houseNumber, zipCode, city, propertyType });
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );
      case 'sales-avm-step3-statbel':
        return (
          <PropertyDetailsForm
            dictionary={dictionary}
            propertyType={sellingState.propertyType || 'apartment'}
            municipality={sellingState.zipCode || ''}
            cityName={sellingState.city || ''}
            onSubmit={async (propertyDetails) => {
              const currentStep = 'sales-avm-step3-statbel';
              setSellingState(prev => ({ ...prev, propertyDetails }));

              // Format the message with property details
              const formattedMessage = dictionary?.sellingForm ? 
                `${dictionary.sellingForm.propertyDetailsProvided}\n` +
                `• ${dictionary.sellingForm.livingArea}: ${propertyDetails.livingArea} m²\n` +
                `• ${dictionary.sellingForm.bedrooms}: ${propertyDetails.bedrooms}\n` +
                `• ${dictionary.sellingForm.bathrooms}: ${propertyDetails.bathrooms}\n` +
                (propertyDetails.plotArea ? `• ${dictionary.sellingForm.plotArea}: ${propertyDetails.plotArea} m²\n` : '') +
                (propertyDetails.facade ? `• ${dictionary.sellingForm.facade}: ${propertyDetails.facade}\n` : '')
                :
                'Here are my property details:\n' +
                `• Living Area: ${propertyDetails.livingArea} m²\n` +
                `• Bedrooms: ${propertyDetails.bedrooms}\n` +
                `• Bathrooms: ${propertyDetails.bathrooms}\n` +
                (propertyDetails.plotArea ? `• Plot Area: ${propertyDetails.plotArea} m²\n` : '') +
                (propertyDetails.facade ? `• Facade: ${propertyDetails.facade}\n` : '');
              handleDeterministicStep(currentStep, formattedMessage, propertyDetails);
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );
      case 'sales-avm-step4-transaction':
        return (
          <PropertyTransactionForm
            dictionary={dictionary}
            agencyLanguage={urlConfig?.language}
            transactionCount={sellingState.propertyDetails?.transactionCount}
            onSubmit={(propertyInfo) => {
              const currentStep = 'sales-avm-step4-transaction';
              setSellingState(prev => ({ ...prev, propertyInfo }));
              const translatedEpcValue = propertyInfo.epcValue === 'unknown' ? dictionary?.sellingForm?.propertyEpcUnknown : propertyInfo.epcValue;
              const conditionKey = `condition${propertyInfo.condition}`; 
              let translatedCondition = dictionary?.propertyForm?.[conditionKey] || propertyInfo.condition;
              // Remove potential duplicated label from the value itself (e.g., "State: 5/5" becomes "5/5")
              if (translatedCondition && translatedCondition.includes(':')) {
                translatedCondition = translatedCondition.split(':').slice(1).join(':').trim();
              }
              
              const finishLevelKeyMap: { [key: string]: string } = {
                'poor': 'finishLevelPoor',
                'moderate': 'finishLevelModerate',
                'good': 'finishLevelGood',
                'very_good': 'finishLevelVeryGood',
              };
              const finishLevelKey = finishLevelKeyMap[propertyInfo.finishLevel];
              const translatedFinishLevel = dictionary?.sellingForm?.[finishLevelKey] || propertyInfo.finishLevel;

              const translatedPropertyInfo: { [key: string]: any } = {
                [dictionary?.sellingForm?.propertyEpc || 'EPC value']: translatedEpcValue,
                [dictionary?.propertyForm?.condition || 'Condition']: translatedCondition,
                [dictionary?.sellingForm?.finishLevel || 'Level of finish']: translatedFinishLevel,
              };
              
              if (propertyInfo.asbestos) {
                const asbestosKeyMap: { [key: string]: string } = {
                  'none': 'asbestosNone',
                  'not_much': 'asbestosNotMuch',
                  'lots': 'asbestosLots',
                  'dont_know': 'asbestosDontKnow',
                };
                const asbestosKey = asbestosKeyMap[propertyInfo.asbestos];
                const translatedAsbestos = dictionary?.sellingForm?.[asbestosKey] || propertyInfo.asbestos;
                translatedPropertyInfo[dictionary?.sellingForm?.asbestos || 'Asbestos'] = translatedAsbestos;
              }

              const formattedMessage = dictionary?.sellingForm ? 
                `${dictionary.sellingForm.propertyProvided || 'Here is my property information:'}\n${Object.entries(translatedPropertyInfo)
                  .map(([key, value]) => `• ${key}: ${value}`)
                  .join('\n')}` :
                `Here are my property details:\n${Object.entries(translatedPropertyInfo)
                  .map(([key, value]) => `• ${key}: ${value}`)
                  .join('\n')}`;

              // ---- New Logic for Step 4 Submission ----
              setError(null); // Clear previous error

              // 1. Construct Payload for /api/avm
              // Ensure all necessary data from sellingState is included
              const avmPayload = {
                'Postal Code': sellingState.zipCode || '',
                'Living Area': Number(sellingState.propertyDetails?.livingArea) || 0,
                'Building Condition Score': propertyInfo.condition ? parseInt(propertyInfo.condition) : 3,
                'Property Type': sellingState.propertyType || 'house',
                'Address': `${sellingState.streetName} ${sellingState.houseNumber}, ${sellingState.zipCode} ${sellingState.city}`,
                'Plot Surface': sellingState.propertyDetails?.plotArea || 0,
                'New Construction': false, // Assuming false unless captured
                'Energy Class': propertyInfo.epcValue || 'unknown',
                'Asbestos': propertyInfo.asbestos,
                'Finish Level': propertyInfo.finishLevel,
                'Number Of Frontages': sellingState.propertyDetails?.facade || (sellingState.propertyType === 'apartment' ? 2 : 4),
                'Bedrooms': sellingState.propertyDetails?.bedrooms || 0,
                'Latitude': sellingState.latitude,
                'Longitude': sellingState.longitude,
                // Add any other required fields for your /api/avm endpoint
              };

              // --- Add a check/log for missing coordinates --- START
              if (sellingState.latitude === undefined || sellingState.longitude === undefined) {
                logger.warn('Latitude or Longitude missing from sellingState before sending AVM payload', {
                  latitude: sellingState.latitude,
                  longitude: sellingState.longitude,
                  currentState: sellingState
                });
                // Potentially add error handling here if needed, though the Python script might still geocode
              }
              // --- Add a check/log for missing coordinates --- END

              // Create the user message object (but don't send yet)
              const newUserMessage: Message = {
                role: 'user',
                content: formattedMessage,
                chatId: chatId,
                userId: guestId,
                classification: { primary_intent: 'provide_data', workflowStep: currentStep },
                workflowStep: 'avm_form'
              };

              // Background task: Save user message, call AVM, update state, call workflow, add next step
              const processStep4 = async () => {
                try {
                  // --- New Flow: Workflow first, then background AVM fetch --- 

                  // Save the user message first
                  if (chatId && guestId) {
                    await saveMessage(newUserMessage);
                  }
                  // Update UI immediately with user message
                  setMessages(prev => [...prev, newUserMessage]);

                  // 1. Call workflow FIRST to get next step ID
                  const workflowResponse = await fetch('/api/avm/workflow', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ currentStep }),
                  });

                  if (!workflowResponse.ok) {
                    throw new Error(`Workflow API returned ${workflowResponse.status}`);
                  }
                  const { nextStep } = await workflowResponse.json();

                  // 2. Initiate AVM fetch in the background (DO NOT AWAIT)
                  const avmPromise = fetch('/api/avm', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ messages: [{ role: 'user', content: JSON.stringify(avmPayload) }] })
                  })
                    .then(async (response) => { // Add basic processing to check status and parse JSON
                      if (!response.ok) {
                        const errorData = await response.json();
                        logger.error('Background AVM fetch failed', { status: response.status, error: JSON.stringify(errorData) });
                        throw new Error(`Background AVM fetch failed: ${response.status} ${JSON.stringify(errorData)}`);
                      }
                      const data = await response.json();
                      // Validate required fields within the promise result
                      if (!data.properties || data.subject_latitude === undefined || data.subject_longitude === undefined) {
                        logger.error('Background AVM fetch response missing required data', { response: data });
                        throw new Error('Background AVM fetch response missing required data.');
                      }
                      return data; // Resolve with the parsed and validated data
                    })
                    .catch(error => {
                      logger.error('Caught error during background AVM fetch promise', { error });
                      // Ensure the promise rejects with an error so await in Step 5 catches it
                      throw error; 
                    });

                  // 3. Store the promise in state
                  setAvmFetchPromise(avmPromise);

                  // 4. Proceed immediately to the next step (Step 5)
                  if (nextStep === 'sales-avm-step5-additional-info') {
                    const nextAssistantMessage: Message = {
                      role: 'assistant',
                      content: '|SHOW_SELLING_FORM',
                      chatId: chatId,
                      userId: agentId as string,
                      classification: { primary_intent: 'sales_avm', workflowStep: nextStep },
                      workflowStep: 'avm_form'
                    };

                    // Add a delay for visual feedback
                    await new Promise(resolve => setTimeout(resolve, 500));

                    // Update UI with next step
                    setMessages(prev => [...prev, nextAssistantMessage]);
                    // Save the assistant message
                    if (chatId && agentId) {
                      await saveMessage(nextAssistantMessage);
                    }
                  } else {
                    logger.warn(`Workflow endpoint returned unexpected step: ${nextStep} after step 4.`);
                    setError('Could not determine the next step automatically.');
                  }

                } catch (error) {
                  // Error handling now focuses on workflow call or saving message
                  logger.error('Error during Step 4 processing (workflow call or saving message):', error);
                  setError(`Failed to process property details: ${error instanceof Error ? error.message : 'Unknown error'}`);
                  // Optionally add an error message to the chat
                }
              };

              // Start the background processing
              processStep4();
              // ---- End New Logic for Step 4 ----
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );

      case 'sales-avm-step5-additional-info':
        return (
          <AdditionalInfoForm
            dictionary={dictionary}
            onSubmit={(additionalDetails: string) => {
              const currentStep = 'sales-avm-step5-additional-info';
              const formattedMessage = dictionary?.sellingForm ? 
                additionalDetails ? 
                  `${dictionary.sellingForm.additionalDetailsProvided || 'Provided details:'}\n${additionalDetails}` : 
                  (dictionary?.sellingForm?.noAdditionalDetailsProvided || 'No additional details provided.')
                :
                additionalDetails ?
                  `Here are my additional details:\n${additionalDetails}` :
                  'No additional details provided.';
               
              // --- Update Logic for Step 5 Submission ---
              setIsLoading(true);
              setError(null);

              // Create user message object
              const newUserMessage: Message = {
                role: 'user',
                content: formattedMessage,
                chatId: chatId,
                userId: guestId,
                classification: { primary_intent: 'provide_data', workflowStep: currentStep },
                workflowStep: 'avm_form'
              };

              // --- Add user message immediately ---
              setMessages(prev => [...prev, newUserMessage]);

              // --- Define background task with updated loader logic --- 
              const processStep5 = async (detailsForReasoning: string) => { 
                try {
                  // --- Save user message (can happen concurrently) --- 
                  if (chatId && guestId) {
                    saveMessage(newUserMessage); // Fire and forget for now
                  }

                  // --- Prepare data for Reasoning Loader --- 
                  const city = sellingState.city || '';
                  const propTypeRaw = sellingState.propertyType || 'property';
                  const propType = propTypeRaw === 'house' ? 
                    (dictionary?.sellingForm?.propertyTypeHouse || 'house') : 
                    (dictionary?.sellingForm?.propertyTypeApartment || 'apartment');
                  const livingArea = sellingState.propertyDetails?.livingArea || 0;
                  const bedrooms = sellingState.propertyDetails?.bedrooms;
                  const bathrooms = sellingState.propertyDetails?.bathrooms;
                  const plotArea = sellingState.propertyDetails?.plotArea;

                  // --- Create and Add the SINGLE Reasoning Loader Message --- 
                  const reasoningStartMessage: Message = {
                    role: 'assistant',
                    content: '|SHOW_REASONING_LOADER', // Flag to render the component
                    chatId: chatId,
                    userId: agentId as string,
                    classification: { primary_intent: 'system_reasoning', workflowStep: 'reasoning_start' }, 
                    properties: { 
                      city,
                      propertyType: propType,
                      livingArea,
                      bedrooms,
                      bathrooms,
                      plotArea,
                      additionalDetails: detailsForReasoning
                    },
                    // Assign temporary ID for React key purposes
                    id: `temp-reasoning-${Date.now()}` 
                  };
                  
                  setMessages(prev => [...prev, reasoningStartMessage]);

                  // --- Calculate total wait time --- 
                  const hasAdditionalDetails = detailsForReasoning && detailsForReasoning.trim().length > 0;
                  const numberOfReasoningSteps = hasAdditionalDetails ? 4 : 3;
                  const totalWaitTime = (numberOfReasoningSteps * 1500);



                  // --- Wait for the calculated duration THEN perform background work & update UI --- 
                  setTimeout(async () => {
                    try {
                      // --- Background work ...
                      if (!avmFetchPromise) {
                        throw new Error('AVM fetch promise not found. Cannot proceed.');
                      }
                      const avmData = await avmFetchPromise;

                      setSellingState(prev => ({
                        ...prev,
                        comparables: avmData.properties,
                        subject_latitude: avmData.subject_latitude,
                        subject_longitude: avmData.subject_longitude,
                        additionalDetails: detailsForReasoning 
                      }));

                      const workflowResponse = await fetch('/api/avm/workflow', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ currentStep }),
                      });

                      if (!workflowResponse.ok) {
                        throw new Error(`Workflow API returned ${workflowResponse.status} after awaiting AVM data`);
                      }
                      const { nextStep } = await workflowResponse.json();

                      if (nextStep === 'sales-avm-step6-map-timeframe') {
                        const nextAssistantMessage: Message = {
                          role: 'assistant',
                          content: '|SHOW_SELLING_FORM',
                          chatId: chatId,
                          userId: agentId as string,
                          classification: { primary_intent: 'sales_avm', workflowStep: nextStep },
                          workflowStep: 'avm_form'
                        };
                        setMessages(prev => [...prev, nextAssistantMessage]); // Just append
                        if (chatId && agentId) {
                          await saveMessage(nextAssistantMessage);
                        }
                      } else {
                        // Handle unexpected step, but DON'T remove the reasoning message
                        logger.warn(`Workflow endpoint returned unexpected step: ${nextStep} after step 5.`);
                        setError('Could not determine the next step automatically.');
                      }
                    } catch (error) {
                      // Handle error, but DON'T remove the reasoning message
                      logger.error('Error during Step 5 background processing (awaiting AVM or workflow):', error);
                      setError(`Failed to process additional info: ${error instanceof Error ? error.message : 'Unknown error'}`);
                    } finally {
                      setIsLoading(false);
                      setAvmFetchPromise(null); 
                    }
                  }, totalWaitTime); 

                } catch (outerError) {
                  logger.error('Error in synchronous part of Step 5 processing:', outerError);
                  setError(`An unexpected error occurred: ${outerError instanceof Error ? outerError.message : 'Unknown error'}`);
                  setIsLoading(false); 
                  setAvmFetchPromise(null); 
                }
              };

              // --- Start background processing, passing details --- 
              processStep5(additionalDetails);
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );

      case 'sales-avm-step6-map-timeframe':
        const streetName = sellingState.streetName?.replace(/\s+\d+.*/, '') || '';
        return (
          <MapTimeframeForm
            dictionary={dictionary}
            streetName={streetName}
            comparables={sellingState.comparables || []}
            subject_latitude={sellingState.subject_latitude}
            subject_longitude={sellingState.subject_longitude}
            excludedTimeframeOptions={urlConfig?.excludedTimeframeOptions}
            onSubmit={({ sellingTimeframe }) => {
              const currentStep = 'sales-avm-step6-map-timeframe';
              setSellingState(prev => ({ ...prev, sellingTimeframe }));
              
              // Find the label for the selected timeframe for a more user-friendly message
              const timeframeOptions = [
                { value: 'now', labelKey: 'timeframeNow', default: 'Now' },
                { value: '6m', labelKey: 'timeframe6m', default: 'In 6 months' },
                { value: '12m', labelKey: 'timeframe12m', default: 'In 12 months' },
                { value: 'curious', labelKey: 'timeframeCurious', default: 'Yes, I don\'t know when yet' },
                { value: 'no', labelKey: 'timeframeNo', default: 'No, I don\'t want to sell' },
              ];
              const selectedOption = timeframeOptions.find(o => o.value === sellingTimeframe);
              const timeframeLabel = dictionary?.sellingForm?.[selectedOption?.labelKey || ''] || selectedOption?.default || sellingTimeframe;
              
              const formattedMessage = `${dictionary?.sellingForm?.sellingTimeframeResponseTitle || 'Selling timeframe'}\n• ${timeframeLabel}`;

              handleDeterministicStep(currentStep, formattedMessage, { sellingTimeframe });
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );

      case 'sales-avm-step7-contact':
        
        const contactPrompt = (urlConfig?.contactPromptOverrideKey && dictionary?.sellingForm?.[urlConfig.contactPromptOverrideKey])
          ? dictionary.sellingForm[urlConfig.contactPromptOverrideKey]
          : dictionary?.sellingForm?.contactPrompt;

        return (
          <ContactForm
            dictionary={dictionary}
            contactPrompt={contactPrompt}
            showNameInput={true}
            agencyId={urlConfig?.agencyId}
            onSubmit={async (contactInfo: ContactInfo) => {
              const currentStep = 'sales-avm-step7-contact';
              setSellingState(prev => ({ ...prev, contactInfo })); // contactInfo now includes name
              // Updated formatted message to include name and phone
              let contactDetailsString = `• ${dictionary?.sellingForm?.contactPhone || 'Phone'}: ${contactInfo.phone}`;
              if (contactInfo.name) {
                contactDetailsString = `• ${dictionary?.sellingForm?.contactName || 'Name'}: ${contactInfo.name}\n` + contactDetailsString;
              }
              const formattedMessage = dictionary?.sellingForm ? 
                `${dictionary.sellingForm.contactProvided || 'Here is my contact information:'}\n${contactDetailsString}` :
                `Here is my contact information:\n${contactDetailsString}`;

              // --- Logic moved from old step 6 ---
              setIsLoading(true);
              setError(null);

              const newUserMessage: Message = {
                role: 'user',
                content: formattedMessage,
                chatId: chatId,
                userId: guestId,
                classification: { primary_intent: 'provide_data', workflowStep: currentStep },
                workflowStep: 'avm_form'
              };

              const processStep7 = async () => {
                try {
                  if (chatId && guestId) {
                    await saveMessage(newUserMessage);
                  }
                  setMessages(prev => [...prev, newUserMessage]);


                  const calculatePayload = {
                    comparables: sellingState.comparables || [],
                    additionalDetails: sellingState.additionalDetails || null,
                    subjectProperty: { 
                      'Postal Code': sellingState.zipCode || '',
                      'Living Area': Number(sellingState.propertyDetails?.livingArea) || 0,
                      'Building Condition Score': sellingState.propertyInfo?.condition ? parseInt(sellingState.propertyInfo.condition) : 3,
                      'Property Type': sellingState.propertyType || 'house',
                      'Address': `${sellingState.streetName} ${sellingState.houseNumber}, ${sellingState.zipCode} ${sellingState.city}`,
                      'Plot Surface': sellingState.propertyDetails?.plotArea || 0,
                      'New Construction': false,
                      'Energy Class': sellingState.propertyInfo?.epcValue || 'unknown',
                      'Asbestos': sellingState.propertyInfo?.asbestos,
                      'Finish Level': sellingState.propertyInfo?.finishLevel,
                      'Number Of Frontages': sellingState.propertyDetails?.facade || (sellingState.propertyType === 'apartment' ? 2 : 4),
                      'Bedrooms': sellingState.propertyDetails?.bedrooms || 0
                    },
                  };

                  const calculateResponse = await fetch('/api/avm/calculate-value', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(calculatePayload)
                  });

                  if (!calculateResponse.ok) {
                    const errorData = await calculateResponse.json();
                    throw new Error(`Calculate Value API failed with status ${calculateResponse.status}. ${JSON.stringify(errorData)}`);
                  }
                  const calculationData = await calculateResponse.json();

                  if (!calculationData.priceRange || !calculationData.priceRange.min || !calculationData.priceRange.max) {
                    logger.error('Calculate Value API response missing priceRange', { response: calculationData });
                    throw new Error('Calculate Value API response missing priceRange.');
                  }
                  


                  const possibleRatings = [4, 4.5, 5];
                  const randomIndex = Math.floor(Math.random() * possibleRatings.length);
                  const calculatedStarRating = possibleRatings[randomIndex];
                  
                  setSellingState(prev => ({
                    ...prev,
                    priceRange: calculationData.priceRange,
                    starRating: calculatedStarRating,
                    contactInfo: contactInfo
                  }));

                  const workflowResponse = await fetch('/api/avm/workflow', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ currentStep }),
                  });

                  if (!workflowResponse.ok) {
                    throw new Error(`Workflow API returned ${workflowResponse.status} after calculating value`);
                  }
                  const { nextStep } = await workflowResponse.json();

                  if (nextStep === 'sales-avm-step8-valuation') {
                    const minPrice = calculationData.priceRange.min;
                    const maxPrice = calculationData.priceRange.max;
                    const valuationTemplate = dictionary?.sellingForm?.valuationMessageContent || 
                      'Based on the details, the estimated value is €[MIN_PRICE] - €[MAX_PRICE].';
                    const valuationContent = valuationTemplate
                      .replace('[MIN_PRICE]', minPrice.toLocaleString(['nl-BE']))
                      .replace('[MAX_PRICE]', maxPrice.toLocaleString(['nl-BE']))
                      + ' |SHOW_SELLING_FORM';

                    const nextAssistantMessage: Message = {
                      role: 'assistant',
                      content: valuationContent,
                      chatId: chatId,
                      userId: agentId as string,
                      classification: { primary_intent: 'sales_avm', workflowStep: nextStep },
                      properties: {
                        priceRange: calculationData.priceRange
                      }
                    };

                    await new Promise(resolve => setTimeout(resolve, 500));
                    setMessages(prev => [...prev, nextAssistantMessage]);
                  } else {
                    logger.warn(`Workflow endpoint returned unexpected step: ${nextStep} after step 7.`);
                    const nextAssistantMessage: Message = {
                      role: 'assistant',
                      content: '|SHOW_SELLING_FORM',
                      chatId: chatId,
                      userId: agentId as string,
                      classification: { primary_intent: 'sales_avm', workflowStep: nextStep || 'unknown' },
                      workflowStep: 'avm_form'
                    };
                    await new Promise(resolve => setTimeout(resolve, 500));
                    setMessages(prev => [...prev, nextAssistantMessage]);
                    if (chatId && agentId) {
                      await saveMessage(nextAssistantMessage);
                    }
                    if (!nextStep) {
                      setError('Could not determine the next step automatically.');
                    }
                  }

                  if (isPhoneVerified) {
                    await triggerAvmEmailNotification();
                  }

                } catch (error) {
                  logger.error('Error during Step 7 processing (Calculate Value or workflow):', error);
                  setError(`Failed to process contact information: ${error instanceof Error ? error.message : 'Unknown error'}`);
                } finally {
                  setIsLoading(false);
                }
              };

              processStep7();
            }}
            disableSubmit={!isCurrentMessage || isLoading}
          />
        );

      case 'sales-avm-step8-valuation':
        
        const handleEmailSubmit = (email: string) => {
          setIsFlowCompleted(true);
        
          // This is a fire-and-forget background task.
          (async () => {
            try {
              const currentState = sellingStateRef.current;
              const updatedContactInfo = {
                ...currentState.contactInfo,
                phone: currentState.contactInfo?.phone || '',
                email: email,
              };
        
              // --- Payload for /api/avm/trigger (if timer was pending) ---
              const { comparables, ...sellingStateWithoutComparables } = currentState;
              const triggerPayload = {
                locale: locale,
                agencyId: urlConfig?.agencyId,
                chatId: chatId,
                guestId: guestId,
                sellingState: {
                  ...sellingStateWithoutComparables,
                  contactInfo: updatedContactInfo,
                },
              };
        
              // --- Payload for /api/avm/trigger-email (only for standard email collection) ---
              const guestEmailPayload = urlConfig?.enableEmailCollection ? {
                locale: locale,
                agencyId: urlConfig?.agencyId,
                agencyName: urlConfig?.agencyName,
                agencyLanguage: urlConfig?.agencyLanguage,
                sellingState: {
                  streetName: currentState.streetName,
                  houseNumber: currentState.houseNumber,
                  zipCode: currentState.zipCode,
                  city: currentState.city,
                  priceRange: currentState.priceRange,
                  contactInfo: { email: email },
                  comparables: currentState.comparables,
                  subject_latitude: currentState.subject_latitude,
                  subject_longitude: currentState.subject_longitude,
                },
              } : null;
        
              // --- Payload for /api/avm/update-lead-contact (if timer already fired) ---
              const updatePayload = {
                guestId: guestId,
                chatId: chatId,
                agencyId: urlConfig?.agencyId,
                locale: locale,
                contactInfo: updatedContactInfo,
              };
              
              // --- Use unified orchestrator action for all email collection types ---
              const action = 'complete_with_update';
              
              // Determine update type and guest email settings
              let updateType: string;
              let shouldSendGuestEmail: boolean;
              
              if (urlConfig?.enableEmailCollectionOnlyAgency) {
                // For agency001: Agency-only email collection
                updateType = 'custom_email';
                shouldSendGuestEmail = false;
              } else {
                // For agency023: Standard email collection with guest email
                updateType = 'email';
                shouldSendGuestEmail = true;
              }
              
              const response = await fetch('/api/avm/orchestrate-post-verification', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  action,
                  data: {
                    guestId,
                    updateType,
                    shouldSendGuestEmail,
                    triggerPayload,
                    guestEmailPayload,
                    updatePayload,
                  },
                }),
              });
              
              if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Orchestrator call failed: ${errorText}`);
              }
              
              logger.info(`Successfully processed ${updateType} update`);
        
              // --- Silently save user/assistant messages for record-keeping ---
              await saveMessage({
                role: 'user',
                content: `Email: ${email}`,
                chatId: chatId,
                userId: guestId,
                workflowStep: 'avm_form'
              });
              await saveMessage({
                role: 'assistant',
                content: '|AVM_FLOW_COMPLETE',
                chatId: chatId,
                userId: agentId as string,
                workflowStep: 'avm_form'
              });
        

            } catch (error) {
              logger.error('Error during orchestrated email submission process:', error);
            }
          })();
        };

        const handleCustomValuationSubmit = () => {
          setIsFlowCompleted(true);

          // Background task
          (async () => {
            try {
              const currentState = sellingStateRef.current;
              const updatedContactInfo = {
                ...currentState.contactInfo,
                phone: currentState.contactInfo?.phone || '',
              };

              // Payload for /api/avm/trigger
              const { comparables, ...sellingStateWithoutComparables } = currentState;
              const triggerPayload = {
                locale: locale,
                agencyId: urlConfig?.agencyId,
                chatId: chatId,
                guestId: guestId,
                userDescription: 'Wants an in-person valuation report', // Add user description for comprehensive email
                sellingState: {
                  ...sellingStateWithoutComparables,
                  contactInfo: updatedContactInfo,
                },
              };

              // Payload for /api/avm/update-lead-description
              const updatePayload = {
                guestId: guestId,
                chatId: chatId,
                agencyId: urlConfig?.agencyId,
                locale: locale,
                contactInfo: updatedContactInfo,
                description: 'Wants an in-person valuation report'
              };

              // Use unified orchestrator action for custom valuation
              const action = 'complete_with_update';
              
              const response = await fetch('/api/avm/orchestrate-post-verification', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  action,
                  data: {
                    guestId,
                    updateType: 'custom_valuation',
                    shouldSendGuestEmail: false,
                    triggerPayload,
                    updatePayload,
                  },
                }),
              });

              if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Orchestrator call failed: ${errorText}`);
              }

              // Save messages for record-keeping
              await saveMessage({
                role: 'user',
                content: 'I want an in-person valuation report',
                chatId: chatId,
                userId: guestId,
                workflowStep: 'avm_form'
              });
              await saveMessage({
                role: 'assistant',
                content: '|CUSTOM_VALUATION_COMPLETE',
                chatId: chatId,
                userId: agentId as string,
                workflowStep: 'avm_form'
              });

            } catch (error) {
              logger.error('Error during custom valuation submission:', error);
            }
          })();
        };

        const handleVerificationSuccess = () => {
        
          if (urlConfig?.enableEmailCollection) {
            
            // This is an async fire-and-forget operation from the user's perspective.
            (async () => {
              try {
                // Ensure guestId is available before proceeding
                if (!guestId) {
                  logger.error('Cannot schedule notification: guestId is missing.');
                  return;
                }

                const currentState = sellingStateRef.current;
                const { comparables, ...sellingStateWithoutComparables } = currentState;
                const sellingStateToSend = {
                  ...sellingStateWithoutComparables,
                  contactInfo: currentState.contactInfo
                };
        
                const triggerPayload = {
                  locale: locale,
                  agencyId: urlConfig?.agencyId,
                  chatId: chatId,
                  guestId: guestId,
                  sellingState: sellingStateToSend,
                };
        
                const response = await fetch('/api/avm/orchestrate-post-verification', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    action: 'schedule',
                    data: {
                      guestId, // Pass guestId at the top level for the orchestrator to use as a key
                      triggerPayload,
                    },
                  }),
                });
        
                if (!response.ok) {
                  const errorText = await response.text();
                  throw new Error(`Failed to schedule notification: ${errorText}`);
                }

              } catch (error) {
                logger.error('Error scheduling server-side notification:', error);
                // Optional: inform user of background failure
              }
            })();
          } else if (urlConfig?.enableEmailCollectionOnlyAgency) {
            
            // For agency001: Schedule timer as fallback, but don't send guest email
            // This is an async fire-and-forget operation from the user's perspective.
            (async () => {
              try {
                // Ensure guestId is available before proceeding
                if (!guestId) {
                  logger.error('Cannot schedule notification: guestId is missing.');
                  return;
                }

                const currentState = sellingStateRef.current;
                const { comparables, ...sellingStateWithoutComparables } = currentState;
                const sellingStateToSend = {
                  ...sellingStateWithoutComparables,
                  contactInfo: currentState.contactInfo
                };
        
                const triggerPayload = {
                  locale: locale,
                  agencyId: urlConfig?.agencyId,
                  chatId: chatId,
                  guestId: guestId,
                  sellingState: sellingStateToSend,
                };
        
                const response = await fetch('/api/avm/orchestrate-post-verification', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    action: 'schedule',
                    data: {
                      guestId, // Pass guestId at the top level for the orchestrator to use as a key
                      triggerPayload,
                    },
                  }),
                });
        
                if (!response.ok) {
                  const errorText = await response.text();
                  throw new Error(`Failed to schedule notification: ${errorText}`);
                }

              } catch (error) {
                logger.error('Error scheduling server-side notification:', error);
                // Optional: inform user of background failure
              }
            })();
            
          } else if (urlConfig?.enableCustomValuationComponent) {
            
            // For agency001 with custom valuation component: Schedule custom timer
            (async () => {
              try {
                // Ensure guestId is available before proceeding
                if (!guestId) {
                  logger.error('Cannot schedule custom valuation notification: guestId is missing.');
                  return;
                }

                const currentState = sellingStateRef.current;
                const { comparables, ...sellingStateWithoutComparables } = currentState;
                const sellingStateToSend = {
                  ...sellingStateWithoutComparables,
                  contactInfo: currentState.contactInfo
                };
        
                const triggerPayload = {
                  locale: locale,
                  agencyId: urlConfig?.agencyId,
                  chatId: chatId,
                  guestId: guestId,
                  sellingState: sellingStateToSend,
                };
        
                const response = await fetch('/api/avm/orchestrate-post-verification', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    action: 'schedule_custom_valuation',
                    data: {
                      guestId, // Pass guestId at the top level for the orchestrator to use as a key
                      triggerPayload,
                      customTimer: urlConfig?.customValuationTimer || 60000 // Pass custom timer with fallback
                    },
                  }),
                });
        
                if (!response.ok) {
                  const errorText = await response.text();
                  throw new Error(`Failed to schedule custom valuation notification: ${errorText}`);
                }

              } catch (error) {
                logger.error('Error scheduling custom valuation notification:', error);
                // Optional: inform user of background failure
              }
            })();
            
          } else {
            // The existing function works perfectly for immediate triggers.
            triggerAvmEmailNotification();
          }
        };

        const handleDetailsResponse = (response: 'result') => {

          // --- Construct the content WITH the price range using dictionary ---
          const minPrice = sellingState.priceRange?.min;
          const maxPrice = sellingState.priceRange?.max;
          // Use a template string from the dictionary if available
          const valuationTemplate = dictionary?.sellingForm?.valuationMessageContent || 
          'Based on the details, the estimated value is €[MIN_PRICE] - €[MAX_PRICE].';
          const valuationContent = valuationTemplate
            .replace('[MIN_PRICE]', minPrice?.toLocaleString(['nl-BE']) || 'N/A') // Use component's locale state
            .replace('[MAX_PRICE]', maxPrice?.toLocaleString(['nl-BE']) || 'N/A') // Use component's locale state
          + ' |SHOW_SELLING_FORM'; // Append the flag

          const primaryIntent = 'valuation_result';

          const silentUserMessage: Message = {
            role: 'assistant',
            content: valuationContent, // <-- Use the new content with price range
            chatId: chatId,
            userId: agentId as string,
            classification: { 
              primary_intent: primaryIntent, 
              workflowStep: 'sales-avm-step8-valuation' 
            },
            workflowStep: 'avm_form',
            // Store raw price range in properties for easier backend access if needed
            properties: {
              priceRange: sellingState.priceRange
            }
          };
          // Save the message but DO NOT add it to the messages state
          saveMessage(silentUserMessage);
        };
        
        return (
          <div className="valuation-form-interaction-area">
            <ValuationForm
              dictionary={dictionary}
              phoneNumber={sellingState.contactInfo?.phone || ''}
              priceRange={sellingState.priceRange}
              // --- Pass verification state and setter ---
              isVerified={isPhoneVerified}
              setIsVerified={setIsPhoneVerified}
              // --- Pass star rating from state --- 
              initialStarRating={sellingState.starRating}
              // --- Pass email collection configuration ---
              enableEmailCollection={urlConfig?.enableEmailCollection ?? false}
              enableEmailCollectionOnlyAgency={urlConfig?.enableEmailCollectionOnlyAgency ?? false}
              customEmailMessage={urlConfig?.customEmailMessage}
              customEmailPrompt={urlConfig?.customEmailPrompt}
              customThankYouMessage={urlConfig?.customThankYouMessage}
              // --- Pass custom valuation component configuration ---
              enableCustomValuationComponent={urlConfig?.enableCustomValuationComponent ?? false}
              customValuationMessage={urlConfig?.customValuationMessage}
              customValuationButton={urlConfig?.customValuationButton}
              customValuationTimer={urlConfig?.customValuationTimer}
              onCustomValuationSubmit={() => {
                handleCustomValuationSubmit();
              }}
              // --- Pass agent contact notice control ---
              agentContactNoticeNull={urlConfig?.agentContactNoticeNull ?? false}
              // --- Pass locale for API calls ---
              locale={locale}
              // --- Add onGoBack prop --- 
              onGoBack={() => {
                
                setMessages(prevMessages => {
                  // 1. Identify indices to remove
                  const lastMessageIndex = prevMessages.length - 1; // Index of current Step 8 trigger
                  let lastStep7AssistantIndex = -1;
                  let lastStep7UserAnswerIndex = -1;

                  // Find the index of the most recent previous Step 7 assistant trigger
                  for (let i = lastMessageIndex - 1; i >= 0; i--) { // Start search before the last message
                    if (prevMessages[i].role === 'assistant' && prevMessages[i].classification?.workflowStep === 'sales-avm-step7-contact') {
                      lastStep7AssistantIndex = i;
                      // Now find the user answer immediately following it
                      if (i + 1 < lastMessageIndex && 
                          prevMessages[i + 1].role === 'user' && 
                          prevMessages[i + 1].classification?.workflowStep === 'sales-avm-step7-contact') {
                        lastStep7UserAnswerIndex = i + 1;
                      } else {
                        logger.warn('Could not find user answer immediately after the last Step 7 assistant message.');
                      }
                      break; // Found the latest one
                    }
                  }

                  // 2. Filter out the identified messages
                  const filteredMessages = prevMessages.filter((_, index) => 
                    index !== lastMessageIndex && 
                    index !== lastStep7AssistantIndex &&
                    index !== lastStep7UserAnswerIndex
                  );

                  // 3. Create the new Step 7 message
                  const step7Message: Message = {
                    role: 'assistant',
                    content: '|SHOW_SELLING_FORM', 
                    chatId: chatId,
                    userId: agentId as string,
                    classification: {
                      primary_intent: 'sales_avm',
                      workflowStep: 'sales-avm-step7-contact'
                    },
                    workflowStep: 'avm_form'
                  };

                  // 4. Return the new array
                  return [...filteredMessages, step7Message]; 
                });
              }}
              // --- Pass trigger function for successful verification --- 
              onVerificationSuccess={handleVerificationSuccess}
              // --- Pass the new handler ---
              onDetailsResponse={handleDetailsResponse} 
              onEmailSubmit={handleEmailSubmit} 
              // --- Pass completion state to persist the success message ---
              isSubmitted={isFlowCompleted}
            />
            {/* --- Comment out the Continue button for Step 8 --- */}
            {/* 
            <button
              onClick={() => {
                const currentStep = 'sales-avm-step8-valuation';
                const confirmationMessage = dictionary?.sellingForm?.valuationConfirmed || 'Thank you for confirming.';
                handleDeterministicStep(currentStep, confirmationMessage, sellingState.priceRange);
              }}
              disabled={isLoading}
              className={`w-fit bg-zinc-900 text-white py-2 px-6 rounded-lg transition-colors mt-4 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-zinc-800'}`}
            >
              {dictionary?.sellingForm?.continueButton || 'Continue'}
            </button>
            */}
          </div>
        );

      case 'sales-avm-step9-final-evaluation':
        return null;

      default:
        return null;
    }
  };

  // Determine current step for progress bar
  const lastFormMessage = messages
    .slice()
    .reverse()
    .find(m => m.role === 'assistant' && m.content?.includes('|SHOW_SELLING_FORM') && m.classification?.workflowStep && AVM_STEPS.includes(m.classification.workflowStep));

  const currentWorkflowStep = lastFormMessage?.classification?.workflowStep;
  const DISPLAY_TOTAL_STEPS = 8; // Still aiming for 8 visual steps

  let progressPercent = 0;
  let currentStepNumber = 0;

  if (currentWorkflowStep) {
    const stepIndex = AVM_STEPS.indexOf(currentWorkflowStep); // Index in the updated 9-step array

    if (stepIndex !== -1) {
      // Map the 9 logical steps (0-8) to 8 visual steps (1-8)
      // Step 0 (Intro) -> Visual Step 1
      // Step 1 (Address) -> Visual Step 2
      // Step 2 (Details) -> Visual Step 3
      // Step 3 (Transaction) -> Visual Step 4
      // Step 4 (Additional Info) -> Visual Step 5
      // Step 5 (Map+Timeframe) -> Visual Step 6
      // Step 6 (Contact) -> Visual Step 7
      // Step 7 (Valuation) -> Visual Step 8
      // Step 8 (Final Eval) -> Visual Step 8 (Completed)

      currentStepNumber = stepIndex + 1; // Start with 1-based index

      // Cap the displayed step number at DISPLAY_TOTAL_STEPS (8)
      if (currentStepNumber > DISPLAY_TOTAL_STEPS) {
        currentStepNumber = DISPLAY_TOTAL_STEPS;
      }

      // Calculate percentage based on the capped step number
      progressPercent = (currentStepNumber / DISPLAY_TOTAL_STEPS) * 100;

    } else if (currentWorkflowStep === 'completed') { // Final state after step 9 logic
      progressPercent = 100;
      currentStepNumber = DISPLAY_TOTAL_STEPS; // Show 8/8
    }
  }

  // --- Add triggerAvmEmailNotification function (adapted from BasicAgentv0) ---
  const triggerAvmEmailNotification = useCallback(async (contactInfoOverride?: ContactInfo) => {

    const currentState = sellingStateRef.current;
    const finalContactInfo = contactInfoOverride || currentState.contactInfo;

    try {
      // Prepare payload for the new endpoint
      const { comparables, ...sellingStateWithoutComparables } = currentState;
      
      // Ensure contactInfo in the payload includes the name
      const sellingStateToSend = {
        ...sellingStateWithoutComparables,
        contactInfo: finalContactInfo
      };

      const payload = {
        locale: locale, // Pass current locale
        agencyId: urlConfig?.agencyId, // Pass agency ID from config
        agentId: urlConfig?.agentId, // Pass agent ID from config
        chatId: chatId, // <-- Add chatId from state
        guestId: guestId, // <-- ADD THIS
        sellingState: sellingStateToSend // Pass the state object WITHOUT comparables, but WITH name in contactInfo
      };

      // --- Check if agencyId is present ---
      if (!payload.agencyId) {
        logger.error('Cannot trigger AVM email: agencyId is missing from urlConfig.');
        // Optionally inform the user or fallback differently?
        // setError("Configuration error: Missing agency identifier.");
        return; // Stop if no agencyId
      }



      const response = await fetch('/api/avm/trigger', { // Use the new endpoint
        method: 'POST', // Ensure method is POST
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload) // Send the collected data
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Error triggering AVM email: API responded with ${response.status}`, { errorText });
        // Optionally show an error to the user?
        // setError("Failed to send valuation summary email.");
      } else {
        const result = await response.json();
      }
    } catch (error) {
      logger.error('Error calling /api/avm/trigger fetch:', error);
      // Optionally show an error to the user?
      // setError("Failed to send valuation summary email.");
    }
  }, [locale, urlConfig, chatId, guestId]); // Add dependencies: sellingState, locale, urlConfig, chatId, guestId

  useEffect(() => {
  }, [locale]);

  // Create a function to send resize message
  const sendResizeMessage = useCallback((newWidth?: string, newHeight?: string) => {
    // Set dimensions based on viewport and chat state
    let width = newWidth;
    let height = newHeight;

    if (!newWidth && !newHeight) {
      if (isMinimized) {
        width = '80px';
        height = '80px';
      } else if (isChatOpen) {
        width = '780px';
        height = '90%';
      } else {
        width = '380px';
        height = '150px';
      }
    }
      
    // Use requestAnimationFrame to prevent flickering
    requestAnimationFrame(() => {
      window.parent.postMessage({
        type: 'RESIZE',
        width,
        height,
        isChatOpen,
        isMinimized
      }, '*');
    });
  }, [isChatOpen, isMinimized]);

  useEffect(() => {
    // Send resize message when chat state changes
    sendResizeMessage();
  }, [sendResizeMessage]);

  // Add this near your other useEffect hooks
  useEffect(() => {
    // Function to handle messages from parent
    const handleParentMessage = (event: MessageEvent) => {
      // --- Add guard to prevent reset if flow is completed ---
      if (isFlowCompleted) {
        return;
      }
      if (event.data.type === 'URL_CHANGED') {
        // The first URL_CHANGED message is our signal to proceed with rendering.
        if (!initialUrlProcessed) {
          setInitialUrlProcessed(true);
        }

        // Handle URL change
        const newUrl = event.data.url;

        // Capture successData if provided (this contains the correct parent page URL)
        if (event.data.successData) {
          setLeadSourceDataFromWidget(event.data.successData);
        }

        try {
          const url = new URL(newUrl);
          const pathSegments = url.pathname.split('/');
          
          // Update locale if it changed
          const newLocale = pathSegments[1]?.toLowerCase().split('-')[0];
          const isLocaleValid = newLocale && /^(en|fr|nl)$/.test(newLocale);
          if (isLocaleValid && newLocale !== locale) {
            setIsReady(false); // Hide widget immediately to prevent flicker
            setLocale(newLocale);
          }
        } catch (error) {
          console.error('Error parsing parent URL:', error);
        } finally {
          sendResizeMessage();
        }
      }
    };

    window.addEventListener('message', handleParentMessage);

    return () => {
      window.removeEventListener('message', handleParentMessage);
    };
  }, [sendResizeMessage, urlConfig, isFlowCompleted, locale, initialUrlProcessed]); 

  // --- Add state for notification visibility ---
  const [showNotification, setShowNotification] = useState(false);

  // --- Add effect to show notification after 5 seconds when minimized ---
  useEffect(() => {
    if (isMinimized && !isTransitioning) {
      // Reset notification state when widget becomes minimized
      setShowNotification(false);
      
      // Show notification after 5 seconds
      const timer = setTimeout(() => {
        setShowNotification(true);
      }, 5000);
      
      return () => clearTimeout(timer);
    } else {
      // Hide notification when widget is not minimized
      setShowNotification(false);
    }
  }, [isMinimized, isTransitioning]);

  // Circular Button Component for minimized state
  const CircularButton = () => (
    <div className="fixed bottom-4 right-4 w-14 h-14 transition-all duration-300 ease-in-out z-[9999]">
      {/* Red notification badge - only show after delay */}
      {showNotification && (
        <div className="absolute -top-1 -right-0 w-4 h-4 bg-red-600 rounded-full flex items-center justify-center border border-white animate-pulse z-10">
          <span className="text-[10px] font-bold text-white">1</span>
        </div>
      )}
      
      <div 
        className="
          w-full h-full
          bg-white 
          rounded-full 
          flex items-center justify-center 
          cursor-pointer 
          shadow-[0_2px_8px_rgba(0,0,0,0.2)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.25)] 
          transition-all duration-300 ease-out
          hover:scale-105 active:scale-95
        "
        onClick={() => {
          // Always ask parent (authoritative store) to clear dismissal state
          try {
            window.parent.postMessage({ type: 'CLEAR_WIDGET_DISMISSAL' }, '*');
          } catch (err) {
            // noop - best effort
          }
          // Best-effort local cleanup in iframe storage as well
          try { localStorage.removeItem('widgetDismissed'); } catch (_) {}
          
          setIsTransitioning(true);
          // Small delay to allow transition animation
          setTimeout(() => {
            setIsMinimized(false);
            setIsChatOpen(true);
            setIsTransitioning(false);
          }, 150);
        }}
      >
        {chatFaviconUrl ? (
          <div className="w-full h-full bg-white rounded-full flex items-center justify-center overflow-hidden">
            <Image 
              src={chatFaviconUrl} 
              alt="Agent Avatar" 
              width={40} 
              height={40} 
              className="rounded-full w-full h-full object-contain" 
              unoptimized={chatFaviconUrl.startsWith('http')} 
            />
          </div>
        ) : (
          <div className="w-full h-full bg-white rounded-full flex items-center justify-center">
            <svg 
              className="w-6 h-6 md:w-7 md:h-7 text-zinc-700" 
              fill="currentColor" 
              viewBox="0 0 24 24"
            >
              <path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z"/>
              <path d="M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 001.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0015.75 7.5z"/>
            </svg>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {!isReady || !isConfigLoaded ? (
        // Render nothing until both dictionary and config are loaded
        null 
      ) : (
        <>
          {isMinimized && !isTransitioning ? (
            <CircularButton />
          ) : (
            <>
              {/* Chat Window - WhatsApp style */}
              {isChatOpen ? (
              // --- Apply mobile full-screen styles first, then override for md+ ---
                <div
                  className={`
                    fixed inset-0 w-screen h-[100dvh] z-[9999] pointer-events-auto
                    transition-all duration-300 ease-out
                    ${isChatOpen
                  ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
                  : 'opacity-0 scale-95 translate-y-8 pointer-events-none'}
                    md:inset-auto md:bottom-6 md:right-6 md:w-[90%] md:h-[90%] md:max-w-md md:max-h-[700px]'
                  `}
                >
                  {/* --- Adjust close button position for mobile vs desktop --- */}
                  <button
                    onClick={minimizeChat}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white hover:bg-gray-200 flex items-center justify-center text-black hover:text-gray-700 transition-colors shadow-sm border border-gray-300 z-10
                                md:-top-2 md:-right-2"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>

                  {/* --- Inner Container: Remove overflow-hidden --- */}
                  <div
                    ref={chatRef}
                    className="w-full h-full bg-white flex flex-col pointer-events-auto font-sans border border-zinc-200
                                md:rounded-xl md:shadow-xl md:overflow-hidden"
                    style={urlConfig?.styling}
                  >
                    {/* --- Header: Adjust padding/rounding for mobile --- */}
                    <div 
                      className="relative flex flex-col px-3 py-4 
                                  md:pt-4 md:pb-4 md:rounded-t-xl"
                    >
                      {/* New Background Layer - Conditionally rendered */}
                      {urlConfig?.headerStyling?.type === 'corner' ? (
                        <div
                          className="absolute inset-0"
                          style={{
                            backgroundColor: urlConfig?.headerStyling?.backgroundColor || '#BD1622',
                            clipPath: 'polygon(0 0, 100% 0, 100% 30%, 0 80%)'
                          }}
                        ></div>
                      ) : urlConfig?.headerStyling?.type === 'gradient' ? (
                        <div 
                          className="absolute inset-0 header-background-layer overflow-hidden"
                          style={{
                            background: urlConfig?.headerStyling?.background || `linear-gradient(to bottom, ${chatBackgroundColor} 20%, transparent 100%)`,
                            backgroundBlendMode: urlConfig?.headerStyling?.backgroundBlendMode || 'normal'
                          }}
                        ></div>
                      ) : (
                        <div 
                          className="absolute inset-0 header-background-layer overflow-hidden"
                          style={{
                            background: `linear-gradient(to bottom, ${chatBackgroundColor} 20%, transparent 100%)`,
                          }}
                        ></div>
                      )}

                      {/* Content goes here, after the background layer */}
                      {/* Add z-10 to ensure content stays above the absolute background */}
                      <div className="relative z-10 flex items-center gap-2"> 
                        {urlConfig?.swapLogoWithAI ? (
                          <div className="w-10 h-10 md:w-8 md:h-8 bg-white rounded-full flex items-center justify-center">
                            <span className="text-base md:text-sm font-semibold text-[#BD1622]">
                              {(urlConfig?.aiChipTextOverrideKey && dictionary?.sellingForm?.[urlConfig.aiChipTextOverrideKey]) || dictionary?.sellingForm?.aiChipText || 'AI'}
                            </span>
                          </div>
                        ) : (
                          chatFaviconUrl && (
                            <div className="w-10 h-10 md:w-8 md:h-8 bg-white rounded-full flex items-center justify-center overflow-hidden">
                              <Image 
                                src={chatFaviconUrl} // Use state directly
                                alt="Agent Avatar" 
                                width={40} 
                                height={40} 
                                className="rounded-full w-full h-full object-contain" // Ensure image fits within wrapper
                                unoptimized={chatFaviconUrl.startsWith('http')} // Disable optimization for external URLs
                              />
                            </div>
                          )
                        )}
                        <div>
                          <h3
                            className="text-lg font-semibold text-gray-900 md:text-base"
                            style={{
                              ...chatTextStyle,
                              color: urlConfig?.headerStyling?.textColor || chatTextStyle?.color
                            }}
                          >
                            {urlConfig?.headerTitleOverrideKey && dictionary?.sellingForm?.[urlConfig.headerTitleOverrideKey]
                              ? dictionary.sellingForm[urlConfig.headerTitleOverrideKey]
                              : dictionary?.sellingForm?.headerTitle || 'Value my home...'}
                          </h3>
                        </div>
                      </div>

                      {/* Progress bar starts here */}
                      {(progressPercent > 0 || currentWorkflowStep === AVM_STEPS[0]) && (
                        <div className="relative z-10 mt-2 max-w-xs w-full mx-auto bg-white p-3 rounded-lg shadow-sm md:mt-2 md:p-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-gray-200 rounded-full">
                              <div
                                className="h-full rounded-full transition-all duration-300"
                                style={{ 
                                  width: `${progressPercent}%`,
                                  backgroundColor: urlConfig?.styling?.backgroundColor || '#18181b'
                                }}
                              ></div>
                            </div>
                            {currentStepNumber > 0 && (
                              <span className="text-sm text-gray-900 font-medium whitespace-nowrap md:text-xs">
                                {dictionary?.chat?.progressStepLabel || 'Step'} {currentStepNumber} / {DISPLAY_TOTAL_STEPS}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div
                      ref={messagesContainerRef}
                      className="flex-1 min-h-0 overflow-y-auto px-2 pt-2 pb-10 space-y-3 md:space-y-4 bg-white md:p-4 md:pb-10"
                      style={urlConfig?.styling}
                    >
                      {messages.map((message, index) => {
                        const isFormMessage = message.role === 'assistant' && message.content?.includes('|SHOW_SELLING_FORM');
                        // --- Check for the reasoning loader flag --- 
                        const isReasoningMessage = message.role === 'assistant' && message.content === '|SHOW_REASONING_LOADER';

                        const assistantBaseClasses = 'bg-gray-100 text-black border border-gray-200';
                        const assistantMobileLayout = isFormMessage ? 'w-full' : 'max-w-[90%] mr-2';
                        const assistantDesktopLayout = 'md:max-w-[85%] md:mr-4';
                          
                        const isLastMessage = index === messages.length - 1;
                          
                        return (
                        // --- Attach ref conditionally, use stable key --- 
                          <div key={message.id || index} className="space-y-2" ref={isLastMessage ? lastMessageRef : null}>
                            <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                              <div
                                className={`rounded-lg px-3 md:px-3 py-2 md:py-2 text-base ${
                                  message.role === 'user'
                                    ? 'bg-zinc-800 text-white max-w-[90%] md:max-w-[85%] ml-2 md:ml-4'
                                    : `${assistantBaseClasses} ${assistantMobileLayout} ${assistantDesktopLayout}`
                                } shadow-sm`}
                                style={{
                                  borderRadius: message.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px'
                                }}
                              >
                                {isFormMessage ? (
                                  renderSellingForm(message)
                                ) : (
                                // --- Render ReasoningLoader if it's the reasoning message --- 
                                  isReasoningMessage ? (
                                    <ReasoningLoader 
                                      dictionary={dictionary} 
                                      // --- Pass data from message properties --- 
                                      city={message.properties?.city || ''}
                                      propertyType={message.properties?.propertyType || ''}
                                      livingArea={message.properties?.livingArea || 0}
                                      // --- Pass new properties with defaults --- 
                                      bedrooms={message.properties?.bedrooms ?? 0}
                                      bathrooms={message.properties?.bathrooms ?? 0}
                                      plotArea={message.properties?.plotArea} // plotArea is already optional
                                      // --- Pass condition and epcValue from sellingState (still needed for loader text) ---
                                      condition={sellingState.propertyInfo?.condition || ''}
                                      epcValue={sellingState.propertyInfo?.epcValue || ''}
                                      asbestos={sellingState.propertyInfo?.asbestos || ''}
                                      finishLevel={sellingState.propertyInfo?.finishLevel || ''}
                                      // --- Pass additional details DIRECTLY from the message properties ---
                                      additionalDetails={message.properties?.additionalDetails}
                                      // --- Pass the overall current workflow step ---
                                      currentAvmWorkflowStep={currentWorkflowStep} // Use the variable calculated for the progress bar
                                    />
                                  ) : (
                                  // --- Otherwise, render standard text message --- 
                                    <div dangerouslySetInnerHTML={{ 
                                      __html: formatMessage(message.content)
                                    }} />
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className={`fixed z-40 pointer-events-auto mx-auto max-w-[350px] bottom-4 w-full md:left-auto md:translate-x-0 md:bottom-6 md:right-6 md:w-auto md:max-w-none transition-all duration-300 ease-in-out hover:scale-[1.025] ${
                    isMinimized 
                      ? 'w-14 h-14 md:w-16 md:h-16 max-w-none rounded-full bottom-4 right-2 md:bottom-4 md:right-2' 
                      : ''
                  } ${isTransitioning ? 'opacity-0 scale-95' : ''}`}
                >
                  {/* Top Component - Positioned directly above the widget */}
                  {urlConfig?.top && (
                    <div className="absolute bottom-full left-0 right-0 mb-2">
                      {React.createElement(urlConfig.top, {
                        onOpenChat: () => setIsChatOpen(true)
                      })}
                    </div>
                  )}

                  {/* Left Component - Positioned directly to the left of the widget */}
                  {urlConfig?.left && (
                    <div className="absolute right-full top-0 bottom-0 mr-2 flex items-center">
                      {React.createElement(urlConfig.left, {
                        onOpenChat: () => setIsChatOpen(true)
                      })}
                    </div>
                  )}

                  <div 
                    className="relative w-full rounded-xl bg-white shadow-xl px-4 py-4 flex flex-col gap-2 cursor-pointer border border-zinc-200 md:p-4 md:w-96"
                    onClick={() => setIsChatOpen(true)}
                    style={urlConfig?.closedWidgetStyling ?? urlConfig?.styling}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        
                        if (urlConfig?.hideWidgetWhenDismissed) {
                          // Completely hide the widget
                          setIsReady(false); // This will unmount the component
                          window.parent.postMessage({
                            type: 'WIDGET_DISMISSED',
                            hideWidgetWhenDismissed: true
                          }, '*');
                        } else {
                          // Current behavior: show circle
                          setIsMinimized(true);
                          setIsChatOpen(false);
                          window.parent.postMessage({
                            type: 'WIDGET_DISMISSED',
                            hideWidgetWhenDismissed: false
                          }, '*');
                        }
                      }}
                      className={`absolute -top-2 -right-2 rounded-full bg-white hover:bg-gray-200 flex items-center justify-center text-black hover:text-gray-700 transition-colors shadow-sm border border-gray-300 z-10 ${
                        urlConfig?.closedWidgetCloseButtonClassName || 'w-5 h-5'
                      }`}
                      aria-label="Close widget" // Add accessibility label
                    >
                      <XMarkIcon className={`${urlConfig?.closedWidgetCloseIconClassName || 'h-3 w-3'}`} />
                    </button>
                      
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className="text-base font-semibold text-gray-900 text-left"
                        style={{ color: (urlConfig?.closedWidgetStyling ?? urlConfig?.styling)?.color }}
                      >
                        {urlConfig?.widgetTitleOverrideKey && dictionary?.sellingForm?.[urlConfig.widgetTitleOverrideKey]
                          ? dictionary.sellingForm[urlConfig.widgetTitleOverrideKey]
                          : dictionary?.sellingForm?.widgetTitle || 'How much is my home worth?'}
                      </p>
                      
                      {/* --- Conditionally render AI chip in top right --- */}
                      {!urlConfig?.swapLogoWithAI && (
                        <span className={`px-3 py-1 rounded-full ml-1 ${urlConfig?.closedWidgetCtaConfig?.AIwhite ? 'bg-white' : 'bg-black'}`}>
                          <span className={`text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r animate-[shimmer_3s_linear_infinite] bg-[length:200%_auto] ${
                            urlConfig?.closedWidgetCtaConfig?.AIwhite 
                              ? 'from-black via-white to-black' 
                              : 'from-white via-zinc-700 to-white'
                          }`}>
                            {(urlConfig?.aiChipTextOverrideKey && dictionary?.sellingForm?.[urlConfig.aiChipTextOverrideKey]) || dictionary?.sellingForm?.aiChipText || 'AI'}
                          </span>
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-1 md:mt-2">
                      {urlConfig?.swapLogoWithArrow ? (
                        // Swapped layout: Logo on left, Arrow on right
                        <>
                          {urlConfig?.swapLogoWithAI ? (
                            <span className="bg-white px-3 py-1 rounded-full">
                              <span className="text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#BD1622] via-white to-[#BD1622] animate-[shimmer_3s_linear_infinite] bg-[length:200%_auto]">
                                {(urlConfig?.aiChipTextOverrideKey && dictionary?.sellingForm?.[urlConfig.aiChipTextOverrideKey]) || dictionary?.sellingForm?.aiChipText || 'AI'}
                              </span>
                            </span>
                          ) : (
                            chatFaviconUrl && (
                              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center overflow-hidden">
                                <Image 
                                  src={chatFaviconUrl} // Use state directly
                                  alt="Agent Avatar" 
                                  width={40} 
                                  height={40} 
                                  className="rounded-full w-full h-full object-contain" 
                                  unoptimized={chatFaviconUrl.startsWith('http')} // Disable optimization for external URLs
                                />
                              </div>
                            )
                          )}

                          <button
                            className={`flex items-center justify-center rounded-full font-medium shadow-md transition-all duration-200 pointer-events-none ${
                              urlConfig?.closedWidgetCtaConfig?.useIcon === false
                                ? 'gap-1 px-3 py-2 text-sm md:gap-2'
                                : 'p-2'
                            } ${
                              urlConfig?.closedWidgetCtaConfig?.colorClassName ||
                              'border-2 border-black text-black'
                            }`}
                          >
                            {urlConfig?.closedWidgetCtaConfig?.useIcon === false ? (
                              <>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                                  <path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z" />
                                  <path d="M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 001.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0015.75 7.5z" />
                                </svg>
                                <span className="font-medium">{dictionary?.sellingForm?.continueButton || 'Start'}</span>
                              </>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                              </svg>
                            )}
                          </button>
                        </>
                      ) : (
                        // Default layout: Arrow on left, Logo on right
                        <>
                          <button
                            className={`flex items-center justify-center rounded-full font-medium shadow-md transition-all duration-200 pointer-events-none ${
                              urlConfig?.closedWidgetCtaConfig?.useIcon === false
                                ? 'gap-1 px-3 py-2 text-sm md:gap-2'
                                : 'p-2'
                            } ${
                              urlConfig?.closedWidgetCtaConfig?.colorClassName ||
                              'border-2 border-black text-black'
                            }`}
                          >
                            {urlConfig?.closedWidgetCtaConfig?.useIcon === false ? (
                              <>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                                  <path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z" />
                                  <path d="M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 001.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0015.75 7.5z" />
                                </svg>
                                <span className="font-medium">{dictionary?.sellingForm?.continueButton || 'Start'}</span>
                              </>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                              </svg>
                            )}
                          </button>

                          {urlConfig?.swapLogoWithAI ? (
                            <span className="bg-white px-3 py-1 rounded-full">
                              <span className="text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#BD1622] via-white to-[#BD1622] animate-[shimmer_3s_linear_infinite] bg-[length:200%_auto]">
                                {(urlConfig?.aiChipTextOverrideKey && dictionary?.sellingForm?.[urlConfig.aiChipTextOverrideKey]) || dictionary?.sellingForm?.aiChipText || 'AI'}
                              </span>
                            </span>
                          ) : (
                            chatFaviconUrl && (
                              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center overflow-hidden">
                                <Image 
                                  src={chatFaviconUrl} // Use state directly
                                  alt="Agent Avatar" 
                                  width={40} 
                                  height={40} 
                                  className="rounded-full w-full h-full object-contain" 
                                  unoptimized={chatFaviconUrl.startsWith('http')} // Disable optimization for external URLs
                                />
                              </div>
                            )
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

export default AvmAgent;